import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  CheckCircle, Circle, Send, RefreshCw, ChevronDown, ChevronUp,
  ChevronLeft, ChevronRight, Calendar, List, Zap, Activity
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import {
  format, parseISO, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isSameDay, isToday, addMonths, subMonths, isBefore
} from 'date-fns';

const fmtDate  = (d) => { try { return d ? format(typeof d === 'string' ? parseISO(d) : new Date(d), 'MMM d') : '-'; } catch { return '-'; } };
const fmtFull  = (d) => { try { return d ? format(typeof d === 'string' ? parseISO(d) : new Date(d), 'EEEE, MMMM d') : '-'; } catch { return '-'; } };
const fmtTime  = (d) => { try { return d ? format(typeof d === 'string' ? parseISO(d) : new Date(d), 'h:mm a') : ''; } catch { return ''; } };

const STAGE_COLORS  = ['bg-blue-100 text-blue-700 border-blue-200','bg-teal-100 text-teal-700 border-teal-200','bg-amber-100 text-amber-700 border-amber-200','bg-orange-100 text-orange-700 border-orange-200'];
const STAGE_DOT     = ['bg-blue-500','bg-teal-500','bg-amber-500','bg-orange-500'];
const STAGE_BG      = ['bg-blue-50 border-blue-200','bg-teal-50 border-teal-200','bg-amber-50 border-amber-200','bg-orange-50 border-orange-200'];
const STAGE_TEXT    = ['text-blue-700','text-teal-700','text-amber-700','text-orange-700'];

const POLL_INTERVAL = 12000; // 12 seconds

export default function Outreach() {
  const [sequences,    setSequences]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');
  const [expanded,     setExpanded]     = useState({});
  const [view,         setView]         = useState('calendar');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay,  setSelectedDay]  = useState(null);
  const [agentActions, setAgentActions] = useState([]);
  const [lastModified, setLastModified] = useState(null);
  const [liveLabel,    setLiveLabel]    = useState('Live');
  const [agentBanner,  setAgentBanner]  = useState(null);
  const prevModifiedRef = useRef(null);

  /* ── load sequences ── */
  const loadSequences = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'All') params.status = statusFilter;
      const res = await api.get('/api/outreach', { params });
      setSequences(res.data || []);
    } catch { if (!silent) toast.error('Failed to load sequences'); }
    finally { if (!silent) setLoading(false); }
  }, [statusFilter]);

  /* ── poll for agent changes ── */
  const pollChanges = useCallback(async () => {
    try {
      const [modRes, actRes] = await Promise.all([
        api.get('/api/outreach/last-modified'),
        api.get('/api/ai/agent/actions/recent?limit=5'),
      ]);
      const newMod = modRes.data?.last_agent_action;
      setAgentActions(actRes.data || []);
      if (newMod && newMod !== prevModifiedRef.current) {
        if (prevModifiedRef.current !== null) {
          // Something changed — refresh silently and show banner
          await loadSequences(true);
          const count = actRes.data?.length || 0;
          setAgentBanner({ time: new Date(), count });
          setLiveLabel('Updated');
          setTimeout(() => setLiveLabel('Live'), 3000);
        }
        prevModifiedRef.current = newMod;
      }
      setLastModified(newMod);
    } catch { /* ignore poll errors */ }
  }, [loadSequences]);

  /* ── initial load ── */
  useEffect(() => { loadSequences(); }, [loadSequences]);

  /* ── polling loop ── */
  useEffect(() => {
    pollChanges();
    const id = setInterval(pollChanges, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [pollChanges]);

  /* ── actions ── */
  const advanceStage = async (contactId) => {
    try { await api.post(`/api/outreach/${contactId}/advance`); toast.success('Stage advanced'); loadSequences(true); }
    catch { toast.error('Failed to advance'); }
  };

  const completeStage = async (contactId, stageNum) => {
    try { await api.post(`/api/outreach/${contactId}/complete-stage?stage_num=${stageNum}`); toast.success('Stage completed'); loadSequences(true); }
    catch { toast.error('Failed'); }
  };

  const toggleExpand = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const getProgress = (stages) => {
    if (!stages?.length) return 0;
    return Math.round((stages.filter(s => s.completed).length / stages.length) * 100);
  };

  /* ── calendar events ── */
  const calendarEvents = useMemo(() => {
    const map = {};
    sequences.forEach(seq => {
      (seq.stages || []).forEach((stage, i) => {
        if (stage.completed) return;
        try {
          const raw = stage.due_date;
          if (!raw) return;
          const d = typeof raw === 'string' ? parseISO(raw) : new Date(raw);
          const key = format(d, 'yyyy-MM-dd');
          if (!map[key]) map[key] = [];
          map[key].push({
            contact_name: seq.contact_name,
            contact_category: seq.contact_category,
            contact_id: seq.contact_id,
            seq_id: seq.id,
            stage: stage.stage,
            day: stage.day,
            task: stage.task,
            stage_index: i,
            seq_status: seq.status,
            is_overdue: isBefore(d, new Date()),
          });
        } catch { /* skip */ }
      });
    });
    return map;
  }, [sequences]);

  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth));
    const end   = endOfWeek(endOfMonth(currentMonth));
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  const selectedKey    = selectedDay ? format(selectedDay, 'yyyy-MM-dd') : null;
  const selectedEvents = selectedKey ? (calendarEvents[selectedKey] || []) : [];

  const overdueCount = Object.entries(calendarEvents)
    .filter(([d]) => d < format(new Date(), 'yyyy-MM-dd'))
    .reduce((acc, [, ev]) => acc + ev.length, 0);

  /* ── recent agent action labels ── */
  const ACTION_LABEL = {
    reschedule_outreach:     '📅 Rescheduled',
    mark_stage_complete:     '✅ Stage done',
    mark_followup_complete:  '✓ Follow-up logged',
    advance_outreach:        '⏭ Advanced',
    snooze_followup:         '⏸ Snoozed',
    bulk_reschedule_overdue: '🗓 Bulk rescheduled',
    add_contact_to_priority: '🎯 Prioritised',
  };

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Outreach Sequences</h1>
          <p className="page-subtitle">Day 1 → Day 7 → Day 21 → Day 45 relationship pipeline</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex bg-muted rounded-lg p-1 gap-1">
            {[['calendar', Calendar, 'Calendar'], ['list', List, 'List']].map(([v, Icon, lbl]) => (
              <button key={v} onClick={() => setView(v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === v ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid={`outreach-${v}-view`}>
                <Icon size={13} />{lbl}
              </button>
            ))}
          </div>
          {view === 'list' && (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Status</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button onClick={() => loadSequences()} variant="outline" size="icon" className="w-9 h-9">
            <RefreshCw size={14} />
          </Button>
        </div>
      </div>

      {/* ── Agent banner ── */}
      {agentBanner && (
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-primary/5 border border-primary/20 text-sm fade-in">
          <Zap size={14} className="text-primary flex-shrink-0" />
          <span className="font-medium text-primary">AI Agent updated the calendar</span>
          <span className="text-muted-foreground">· {format(agentBanner.time, 'h:mm a')} · calendar refreshed automatically</span>
          <button onClick={() => setAgentBanner(null)} className="ml-auto text-muted-foreground hover:text-foreground text-xs">Dismiss</button>
        </div>
      )}

      {/* ── Stage legend + live indicator ── */}
      <div className="flex flex-wrap items-center gap-2">
        {[['Day 1','Personal follow-up'],['Day 7','Check-in'],['Day 21','Advance'],['Day 45','Stalled']].map(([day, desc], i) => (
          <div key={day} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${STAGE_COLORS[i]}`}>
            <span className="font-bold">{day}</span><span>{desc}</span>
          </div>
        ))}
        <div className="ml-auto flex items-center gap-3 text-xs">
          {overdueCount > 0 && <span className="badge-overdue px-2 py-0.5 rounded-full font-medium">{overdueCount} overdue</span>}
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <span className={`w-2 h-2 rounded-full ${liveLabel === 'Updated' ? 'bg-teal-500 animate-pulse' : 'bg-green-500'}`} />
            {liveLabel} · {sequences.length} sequences
          </span>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : sequences.length === 0 ? (
        <Card><CardContent className="text-center py-12">
          <Send size={40} className="mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No sequences yet. Add contacts to create sequences automatically.</p>
        </CardContent></Card>
      ) : view === 'calendar' ? (

        /* ══════════════ CALENDAR VIEW ══════════════ */
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">

          {/* Calendar grid */}
          <div className="xl:col-span-2">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle style={{ fontFamily:'Cormorant Garamond,serif' }} className="text-xl">
                    {format(currentMonth, 'MMMM yyyy')}
                  </CardTitle>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="icon" className="w-8 h-8" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}><ChevronLeft size={13} /></Button>
                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setCurrentMonth(new Date())}>Today</Button>
                    <Button variant="outline" size="icon" className="w-8 h-8" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}><ChevronRight size={13} /></Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* Day headers */}
                <div className="grid grid-cols-7 border-b border-border">
                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                    <div key={d} className="py-2 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">{d}</div>
                  ))}
                </div>
                {/* Grid */}
                <div className="grid grid-cols-7">
                  {calendarDays.map((day, idx) => {
                    const key    = format(day, 'yyyy-MM-dd');
                    const events = calendarEvents[key] || [];
                    const inMonth  = isSameMonth(day, currentMonth);
                    const selected = selectedDay && isSameDay(day, selectedDay);
                    const todayDay = isToday(day);
                    const hasOverdue = events.some(e => e.is_overdue);
                    const isLast = idx >= calendarDays.length - 7;
                    return (
                      <button key={key}
                        onClick={() => setSelectedDay(isSameDay(day, selectedDay || new Date(0)) ? null : day)}
                        className={`min-h-[88px] p-1.5 text-left border-r border-b border-border transition-colors
                          ${!inMonth ? 'bg-muted/20' : 'bg-card hover:bg-muted/30'}
                          ${selected ? 'ring-1 ring-inset ring-primary/40 bg-accent/20' : ''}
                          ${isLast ? 'border-b-0' : ''} ${idx % 7 === 6 ? 'border-r-0' : ''}`}>
                        {/* Day number */}
                        <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-semibold mb-1
                          ${todayDay ? 'bg-primary text-primary-foreground' : inMonth ? 'text-foreground' : 'text-muted-foreground/30'}
                          ${hasOverdue && !todayDay ? 'ring-1 ring-red-400' : ''}`}>
                          {format(day, 'd')}
                        </div>
                        {/* Event chips */}
                        <div className="space-y-0.5">
                          {events.slice(0, 3).map((ev, i) => (
                            <div key={i}
                              className={`text-xs px-1.5 py-0.5 rounded truncate font-medium border
                                ${ev.is_overdue ? 'bg-red-50 border-red-200 text-red-700' : `${STAGE_BG[ev.stage_index]} ${STAGE_TEXT[ev.stage_index]}`}`}
                              title={`${ev.contact_name} — Day ${ev.day}`}>
                              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${ev.is_overdue ? 'bg-red-500' : STAGE_DOT[ev.stage_index]}`} />
                              {ev.contact_name.split(',')[0].split(' ')[0]}
                            </div>
                          ))}
                          {events.length > 3 && <div className="text-xs text-muted-foreground px-1">+{events.length - 3} more</div>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right panel: day detail + agent activity */}
          <div className="space-y-4">

            {/* Day detail */}
            <Card className="sticky top-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-base" style={{ fontFamily:'Cormorant Garamond,serif' }}>
                  {selectedDay ? fmtFull(selectedDay.toISOString()) : 'Select a day'}
                </CardTitle>
                {selectedDay && isToday(selectedDay) && <Badge variant="secondary" className="w-fit text-xs">Today</Badge>}
              </CardHeader>
              <CardContent className="pt-0">
                {!selectedDay ? (
                  <div className="text-center py-6">
                    <Calendar size={28} className="mx-auto mb-2 text-muted-foreground/30" />
                    <p className="text-xs text-muted-foreground">Click any date to see outreach due</p>
                  </div>
                ) : selectedEvents.length === 0 ? (
                  <div className="text-center py-6">
                    <CheckCircle size={28} className="mx-auto mb-2 text-muted-foreground/30" />
                    <p className="text-xs text-muted-foreground">No outreach due this day</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {selectedEvents.map((ev, i) => (
                      <div key={i} className={`p-3 rounded-lg border ${ev.is_overdue ? 'bg-red-50 border-red-200' : STAGE_BG[ev.stage_index]}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className={`font-semibold text-sm truncate ${ev.is_overdue ? 'text-red-700' : STAGE_TEXT[ev.stage_index]}`}>
                              {ev.contact_name}
                            </div>
                            <div className="text-xs text-muted-foreground">{ev.contact_category}</div>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-bold border flex-shrink-0 ${ev.is_overdue ? 'bg-red-100 border-red-300 text-red-700' : STAGE_COLORS[ev.stage_index]}`}>
                            Day {ev.day}
                          </span>
                        </div>
                        <div className={`text-xs mt-1 font-medium ${ev.is_overdue ? 'text-red-600' : STAGE_TEXT[ev.stage_index]}`}>{ev.task}</div>
                        <Button size="sm" variant="outline" className="w-full mt-2 h-7 text-xs"
                          onClick={() => completeStage(ev.contact_id, ev.stage)}>
                          <CheckCircle size={11} className="mr-1" /> Mark Complete
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>

              {/* Month overview */}
              {!selectedDay && (
                <CardContent className="pt-0 border-t border-border">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{format(currentMonth, 'MMMM')} Overview</p>
                  <div className="space-y-1.5">
                    {[0,1,2,3].map(si => {
                      const cnt = Object.entries(calendarEvents)
                        .filter(([d]) => d.startsWith(format(currentMonth,'yyyy-MM')))
                        .reduce((a,[,evs]) => a + evs.filter(e => e.stage_index === si).length, 0);
                      if (!cnt) return null;
                      return (
                        <div key={si} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${STAGE_DOT[si]}`} /><span className="text-muted-foreground">Day {[1,7,21,45][si]}</span></div>
                          <span className="font-semibold tabular-nums">{cnt}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Agent activity feed */}
            {agentActions.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Zap size={13} className="text-primary" /> Agent Activity
                    <span className="ml-auto text-xs text-muted-foreground font-normal">auto-updating</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-1.5">
                    {agentActions.slice(0,6).map((a, i) => (
                      <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-muted/50 text-xs">
                        <Zap size={11} className="text-primary mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-foreground truncate">
                            {ACTION_LABEL[a.action?.type] || a.action?.type}
                          </div>
                          <div className="text-muted-foreground truncate">{a.action?.contact_name?.split(',')[0]}</div>
                          <div className="text-muted-foreground/60">{a.action?.detail}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

      ) : (
        /* ══════════════ LIST VIEW ══════════════ */
        <div className="space-y-3">
          {sequences.map(seq => (
            <Card key={seq.id} className="overflow-hidden" data-testid={`outreach-seq-${seq.id}`}>
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{seq.contact_name}</div>
                      <div className="text-xs text-muted-foreground">{seq.contact_category}</div>
                    </div>
                    {(() => {
                      const s = { in_progress:'badge-on-track', completed:'bg-muted text-muted-foreground', stalled:'badge-overdue' };
                      return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s[seq.status]||''}`}>{seq.status?.replace('_',' ')}</span>;
                    })()}
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <div className="w-20">
                      <Progress value={getProgress(seq.stages)} className="h-1.5" />
                      <div className="text-xs text-muted-foreground text-center mt-0.5">{getProgress(seq.stages)}%</div>
                    </div>
                    {seq.status === 'in_progress' && (
                      <Button size="sm" variant="outline" onClick={() => advanceStage(seq.contact_id)} className="text-xs h-7">Advance</Button>
                    )}
                    <Button variant="ghost" size="icon" className="w-7 h-7" onClick={() => toggleExpand(seq.id)}>
                      {expanded[seq.id] ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {expanded[seq.id] && (
                <CardContent className="pt-0 px-4 pb-4">
                  <div className="space-y-2">
                    {seq.stages?.map((stage, i) => {
                      let dueDateStr = '-';
                      try { dueDateStr = stage.due_date ? fmtDate(stage.due_date) : '-'; } catch {}
                      return (
                        <div key={stage.stage}
                          className={`flex items-center gap-3 p-2.5 rounded-lg ${stage.completed ? 'bg-muted/30' : seq.current_stage===stage.stage ? 'bg-accent/30 border border-accent' : 'bg-muted/20'}`}>
                          <button onClick={() => !stage.completed && completeStage(seq.contact_id, stage.stage)}
                            className={`flex-shrink-0 ${stage.completed ? 'text-green-600' : 'text-muted-foreground hover:text-primary'}`}>
                            {stage.completed ? <CheckCircle size={17} /> : <Circle size={17} />}
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm font-medium ${stage.completed ? 'line-through text-muted-foreground' : ''}`}>
                              Day {stage.day}: {stage.task}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Due: {dueDateStr}{stage.completed_at && ` · Completed: ${fmtDate(stage.completed_at)}`}
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${STAGE_COLORS[i]}`}>Stage {stage.stage}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
